import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { demoData } from '../data/demoData'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

function isMissingTable(error, table) {
  const message = String(error?.message || '').toLowerCase()
  return error?.code === '42P01'
    || error?.code === 'PGRST205'
    || (message.includes(table) && (message.includes('does not exist') || message.includes('could not find')))
}

export function useSummitData() {
  const [data, setData] = useState({ ...demoData, photos: [] })
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [error, setError] = useState('')
  const [routeLegsError, setRouteLegsError] = useState('')
  const [routeLegsReady, setRouteLegsReady] = useState(false)
  const [photosError, setPhotosError] = useState('')
  const [photosReady, setPhotosReady] = useState(false)
  const [cloudEmpty, setCloudEmpty] = useState(false)
  const [session, setSession] = useState(null)
  const reloadTimerRef = useRef(null)

  const loadData = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      setData({ ...demoData, photos: [] })
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')
    setRouteLegsError('')
    setPhotosError('')

    const [tripsResult, summitsResult, routeResult] = await Promise.all([
      supabase.from('trips').select('*').order('start_date', { ascending: true, nullsFirst: false }),
      supabase.from('summits').select('*').order('country_name'),
      supabase.from('route_points').select('*').order('position'),
    ])

    const firstError = tripsResult.error || summitsResult.error || routeResult.error
    if (firstError) {
      setError(firstError.message)
      setData({ ...demoData, photos: [] })
      setLoading(false)
      return
    }

    let routeLegs = []
    const routeLegsResult = await supabase.from('route_legs').select('*').order('created_at', { ascending: true })
    if (routeLegsResult.error) {
      setRouteLegsReady(false)
      setRouteLegsError(isMissingTable(routeLegsResult.error, 'route_legs')
        ? 'Run supabase/route-legs-upgrade.sql once to enable exact Google routes.'
        : routeLegsResult.error.message)
    } else {
      routeLegs = routeLegsResult.data || []
      setRouteLegsReady(true)
    }

    let photos = []
    const photosResult = await supabase.from('photos').select('*').order('captured_at', { ascending: true, nullsFirst: false })
    if (photosResult.error) {
      setPhotosReady(false)
      setPhotosError(isMissingTable(photosResult.error, 'photos')
        ? 'Run supabase/photo-map-upgrade.sql once to enable photo maps.'
        : photosResult.error.message)
    } else {
      photos = photosResult.data || []
      setPhotosReady(true)
    }

    const cloudData = {
      trips: tripsResult.data || [],
      summits: summitsResult.data || [],
      routePoints: routeResult.data || [],
      routeLegs,
      photos,
    }

    const empty = cloudData.trips.length === 0 && cloudData.summits.length === 0
    setCloudEmpty(empty)
    setData(empty ? { ...demoData, photos: [] } : cloudData)
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!supabase) return undefined
    supabase.auth.getSession().then(({ data: authData }) => setSession(authData.session))
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession))
    return () => listener.subscription.unsubscribe()
  }, [])

  useEffect(() => { loadData() }, [loadData])

  // Realtime fires one event per changed ROW. Reordering a nine-stop route
  // writes every row twice, so an un-debounced handler ran eighteen full
  // reloads — and, because the collections it produces are map effect
  // dependencies, eighteen complete map rebuilds. That was the multi-second
  // freeze on every drag. Coalescing the burst into a single reload fixes it
  // without touching any rendering code.
  const scheduleReload = useCallback(() => {
    window.clearTimeout(reloadTimerRef.current)
    reloadTimerRef.current = window.setTimeout(() => loadData(), 400)
  }, [loadData])

  useEffect(() => {
    if (!supabase) return undefined
    const channel = supabase
      .channel('summit-hub-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trips' }, scheduleReload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'summits' }, scheduleReload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'route_points' }, scheduleReload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'route_legs' }, scheduleReload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'photos' }, scheduleReload)
      .subscribe()
    return () => {
      window.clearTimeout(reloadTimerRef.current)
      supabase.removeChannel(channel)
    }
  }, [scheduleReload])

  const source = useMemo(() => {
    if (!isSupabaseConfigured) return 'demo'
    if (cloudEmpty) return 'demo-connected'
    return 'cloud'
  }, [cloudEmpty])

  return {
    ...data,
    routeLegs: data.routeLegs || [],
    photos: data.photos || [],
    loading,
    error,
    routeLegsError,
    routeLegsReady,
    photosError,
    photosReady,
    source,
    cloudEmpty,
    session,
    refresh: loadData,
  }
}
